/* =============================================================
   ROUTES
   =============================================================
   Every path in the app, in one object. Import `routes` instead of
   typing a URL string anywhere — renaming a route then touches one
   line rather than a dozen components.

   Static paths live in `routes`; anything with an :id is a pattern
   for the router (`patterns`) and a builder for links (`to`), kept
   apart so no component ever interpolates a URL by hand. The
   detail patterns only match digits, so `/locations/new` can never
   fall through to the detail page.
   ============================================================= */

export const routes = {
  // Public / unauthenticated
  signIn: '/sign-in',
  /** Not a page: redirects to sign-in. Accounts are set up by River Atlas. */
  signUp: '/sign-up',

  // Authenticated
  dashboard: '/dashboard',
  search: '/search',
  compare: '/search/compare',
  locations: '/locations',
  locationNew: '/locations/new',
  availability: '/availability',
  vessels: '/vessels',
  vesselNew: '/vessels/new',
  bookings: '/bookings',
  reports: '/reports',
  settings: '/settings',
} as const

/** Router patterns. Not for linking — use `to` below. */
export const patterns = {
  /** A berth as a ship operator sees it: profile, price, book. */
  berthProfile: '/berths/:id(\\d+)',
  book: '/berths/:id(\\d+)/book',
  /** A berth as its owner sees it: tabs for details, rates, availability, bookings. */
  berth: '/locations/:id(\\d+)',
  berthEdit: '/locations/:id(\\d+)/edit',
  berthRateNew: '/locations/:id(\\d+)/rates/new',
  berthRateEdit: '/locations/:id(\\d+)/rates/:rateId(\\d+)/edit',
  berthBlockNew: '/locations/:id(\\d+)/blocks/new',
  vessel: '/vessels/:id(\\d+)',
  vesselEdit: '/vessels/:id(\\d+)/edit',
  booking: '/bookings/:id(\\d+)',
  bookingConfirmation: '/bookings/:id(\\d+)/confirmation',
} as const

export type BerthTab = 'details' | 'rates' | 'availability' | 'bookings'

/** The search a ship operator is in the middle of, carried between screens. */
export interface SearchContext {
  city?: string
  vessel?: number
  /** ISO instants. */
  from?: string
  to?: string
  amenities?: string[]
}

/** As a query object: absent values left out, amenities comma-joined. */
export function searchQuery(ctx: SearchContext): Record<string, string> {
  const q: Record<string, string> = {}
  if (ctx.city) q.city = ctx.city
  if (ctx.vessel) q.vessel = String(ctx.vessel)
  if (ctx.from) q.from = ctx.from
  if (ctx.to) q.to = ctx.to
  if (ctx.amenities && ctx.amenities.length > 0) q.amenities = ctx.amenities.join(',')
  return q
}

/** Link builders for the parameterised routes. */
export const to = {
  // Owner side
  berth: (id: number) => `/locations/${id}`,
  berthEdit: (id: number) => `/locations/${id}/edit`,
  /** A berth page opened on one of its tabs. 'details' is the default, so no query. */
  berthTab: (id: number, tab: BerthTab) =>
    tab === 'details' ? `/locations/${id}` : `/locations/${id}?tab=${tab}`,
  berthRateNew: (id: number) => `/locations/${id}/rates/new`,
  berthRateEdit: (id: number, rateId: number) => `/locations/${id}/rates/${rateId}/edit`,
  berthBlockNew: (id: number) => `/locations/${id}/blocks/new`,

  // Ship-operator side
  berthProfile: (id: number, ctx: SearchContext = {}) => ({ path: `/berths/${id}`, query: searchQuery(ctx) }),
  book: (id: number, ctx: SearchContext = {}) => ({ path: `/berths/${id}/book`, query: searchQuery(ctx) }),
  search: (ctx: SearchContext = {}) => ({ path: routes.search, query: searchQuery(ctx) }),
  compare: (ids: number[], ctx: SearchContext = {}) => ({
    path: routes.compare,
    query: { ...searchQuery(ctx), ids: ids.join(',') },
  }),

  vessel: (id: number) => `/vessels/${id}`,
  vesselEdit: (id: number) => `/vessels/${id}/edit`,

  booking: (id: number) => `/bookings/${id}`,
  bookingConfirmation: (id: number) => `/bookings/${id}/confirmation`,
} as const

export type RoutePath = (typeof routes)[keyof typeof routes]

/** Where a user lands right after signing in, and what "/" resolves to. */
export const HOME_ROUTE: RoutePath = routes.dashboard
